// The words of the live deal line and card (LiveCard.tsx; pipeline P5; DEALS-LIVE-CARD-BRIEF-2026-10-04.md section 5,
// drawn in a-deals.html and states.html), kept pure so each is tested (live-words.test.ts). Every string is built from
// GET /api/deals/heat (U152); none is a placeholder.
import { dayMonth } from '../day.js';
import type { CobrainMove, DealAct, DealMessage, HeatMoney, HeatRow, HistoryChange, Lead, Named, NewToday, Opens, StageStep, TeamDecision } from './api.js';
import { firstName, moneyFull, possessive } from './heat-words.js';

/** The navy step a keenness wears, each of 5 to 1 its own (U268, U269), or the neutral block when there is none. */
export type KeenClass = 'k5' | 'k4' | 'k3' | 'k2' | 'k1' | 'k0';
export function keenClass(k: number | null | undefined): KeenClass {
  if (k === null || k === undefined) return 'k0';
  return k >= 5 ? 'k5' : k === 4 ? 'k4' : k === 3 ? 'k3' : k === 2 ? 'k2' : 'k1';
}

/**
 * The colour family a stage wears on Deals (U299, U300; DEALS-COLOUR-BRIEF-2026-10-05.md section 5), from the store's own
 * stage code: Talking grey, Send proposal strong magenta, Proposal sent soft magenta, Contracting and Signed green. A code
 * the ladder does not name (none yet, or a retired one) wears Talking's grey. The word and the bars carry it, set once
 * on the element that holds them (`data-stage`), so the stage is told by its word and its count of bars as well.
 */
export type StageTone = 'talk' | 'send' | 'sent' | 'contract' | 'signed';
export function stageTone(code: string | null | undefined): StageTone {
  switch (code) {
    case 'send_proposal': return 'send';
    case 'paper_out': case 'terms': return 'sent';
    case 'to_sign': return 'contract';
    case 'signed': return 'signed';
    default: return 'talk';
  }
}

/**
 * Whose news it is (U307): what the client did (their messages, a meeting, a call) leads in ink; Cobrain's own reading
 * (a stage moved, a keenness read) is quieter; what our side wrote is the lightest.
 */
export type NewsKind = 'msgs' | 'moved' | 'you';
export function newsKind(n: NewToday): NewsKind {
  if (n.what === 'moved' || n.what === 'keenness') return 'moved';
  if (n.what === 'messages' || !n.message) return 'msgs';
  const m = n.message;
  if (m.kind === 'meeting' || m.kind === 'call') return 'msgs';
  return m.fromMe || m.direction === 'outbound' || m.kind === 'manual_note' ? 'you' : 'msgs';
}

/** A day as the card says it: "today" from the reader's own midnight (the API's), else "3 Oct". Never "yesterday" from
 *  hours (B1300): a day is the reader's calendar's, and only the API knows their midnight. */
export function dayWords(at: string, dayStart: string | null): string {
  return dayStart !== null && at >= dayStart ? 'today' : dayMonth(new Date(at));
}

/** A message's chip on the Latest list. */
export function kindChip(kind: string): string {
  switch (kind) {
    case 'email': return 'Email';
    case 'teams': return 'Teams';
    case 'call': return 'Call';
    case 'meeting': return 'Meeting';
    case 'whatsapp': return 'WhatsApp';
    case 'messenger': return 'Cobrain';
    case 'note': case 'manual_note': return 'Note';
    default: { const w = kind.replace(/_/g, ' '); return w.charAt(0).toUpperCase() + w.slice(1); }
  }
}

/**
 * A message's subject as the page says it (U32): a note filed under the name of the file it came from ("Note:
 * SBC_Summit_2026_Master_Plan") is said in words ("Note: SBC Summit 2026 Master Plan"); any other subject as it is.
 */
export function subjectWords(m: Pick<DealMessage, 'kind' | 'subject'>): string | null {
  if (m.subject === null || (m.kind !== 'note' && m.kind !== 'manual_note')) return m.subject;
  return m.subject.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim();
}

function kindNoun(kind: string): string {
  switch (kind) {
    case 'email': return 'email';
    case 'teams': return 'Teams message';
    case 'call': return 'call';
    case 'meeting': return 'meeting';
    case 'whatsapp': return 'WhatsApp message';
    case 'messenger': return 'message';
    default: return kind;
  }
}

/** Who a message is from, said from the reader's side. */
export function senderWords(m: DealMessage): string {
  return m.fromMe ? 'You' : m.from ?? 'Someone';
}

/**
 * A message as the source of something Cobrain read: "your email to Ana Ruiz with Proposal.pdf attached", "Ana Ruiz's
 * call", "a meeting". The brief's "from your email to Ana Ruiz with the proposal attached".
 */
export function messageWords(m: DealMessage): string {
  const noun = kindNoun(m.kind);
  const whose = m.fromMe ? `your ${noun}${m.to ? ` to ${m.to}` : ''}` : m.from ? `${possessive(m.from)} ${noun}` : `${/^[aeiou]/i.test(noun) ? 'an' : 'a'} ${noun}`;
  return m.attachment ? `${whose} with ${m.attachment} attached` : whose;
}

/** A client's message reads as a reply when its subject says so: Re:, and its German and Nordic forms AW: and SV:. */
const isReply = (m: DealMessage): boolean => /^\s*(re|aw|sv)\s*:/i.test(m.subject ?? '');

/** One message as news: "Ana Ruiz replied", "You wrote to Ana Ruiz", "A call with Ana Ruiz". `short` takes first names. */
function messageNews(m: DealMessage): string {
  if (m.kind === 'call') return m.fromMe ? `Your call${m.to ? ` with ${m.to}` : ''}` : `A call${m.from ? ` with ${m.from}` : ''}`;
  if (m.kind === 'meeting') return `A meeting${m.subject ? `: ${m.subject}` : ''}`;
  if (m.fromMe) return `You wrote${m.to ? ` to ${m.to}` : ''}`;
  if (m.direction === 'outbound') return `${m.from || 'A colleague'} wrote${m.to ? ` to ${m.to}` : ''}`;
  return `${m.from || 'A client'} ${isReply(m) ? 'replied' : 'wrote'}`;
}

/** The line's "what is new today" (the brief, section 5 item 6): "Ana Ruiz replied, today", "3 messages, today". */
export function newTodayWords(n: NewToday): string {
  switch (n.what) {
    case 'moved': return `Moved to ${n.toName ?? 'a new stage'}, today`;
    case 'keenness': return `Keenness read as ${n.toName ?? '?'}, today`;
    case 'messages': return `${n.count} messages, today`;
    case 'message': return `${n.message ? messageNews(n.message) : 'A message'}, today`;
  }
}

/**
 * The silence a line shows (BUGS.md D141): the API's count, except on a parked deal before its date has passed, which
 * reads its parked words alone. Mike, by pop-up 6 Oct 12:06 CEST (FT2): "Parked words only (Recommended)", the line reading
 * the parked words with no silence warning until the park's date has passed. A park with no date has not passed.
 */
export function shownSilence(r: Pick<HeatRow, 'silentDays' | 'lifecycle' | 'pausedUntil'>, now: string): number | null {
  if (r.silentDays === null) return null;
  if (r.lifecycle === 'paused' && (r.pausedUntil === null || Date.parse(r.pausedUntil) > Date.parse(now))) return null;
  return r.silentDays;
}

/** "Silent for 15 days" (D68's count, the API's number). */
export function silentWords(days: number): string {
  return `Silent for ${days} ${days === 1 ? 'day' : 'days'}`;
}

function stepOwner(owner: Named | null): string {
  if (owner?.mine) return 'Your step';
  const n = firstName(owner);
  return n ? `${possessive(n)} step` : 'A step';
}

/**
 * The show notes' owner of a step, possessive, with every name it holds (BUGS.md D14): "Vinny (intro); Mike" and
 * "Milan, Chris" are two people, said "Vinny (intro) and Mike's", "Milan and Chris's", where they printed as one name
 * ("Vinny (intro); Mike's step"). Text with one name, "Will" or "Tom or Vinny", reads as written.
 */
export function notesOwners(who: string): string {
  const names = who.split(/\s*[;,&/]\s*/).map((n) => n.trim()).filter(Boolean);
  if (names.length < 2) return possessive(who);
  return possessive(`${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`);
}

/**
 * A line's status (the brief DEALS-ONE-LIST-BRIEF-2026-10-05.md, section 3, "what used to be a band"): what holds its next
 * step, what the client owes, whose step it is ("Your step: ...", "Tom's step: ..."), else the show notes' step in grey
 * until one is accepted (U230), else none. `you` marks the reader's own step, said in ink. The company record's line for
 * an opportunity says the same (U240).
 */
export function rowStep(r: HeatRow): StepWords {
  const parked = parkedWords(r);
  if (parked) return parked;
  const held = r.holds.filter((h) => h.blocks === 'next_step');
  if (held.length > 0) return said('us', 'Waiting on us:', held[0]!.title);
  if (r.promises.length > 0) return said('client', 'Waiting on the client:', r.promises[0]!.statement);
  if (r.step) return said(r.step.owner?.mine === true ? 'mine' : 'step', `${stepOwner(r.step.owner)}:`, r.step.title);
  const noted = r.fromSheet?.nextStep?.trim();
  if (noted) {
    const who = r.fromSheet?.nextStepOwner?.trim();
    return said('notes', null, `${who ? `${notesOwners(who)} step, from the show notes` : 'From the show notes'}: ${noted}`);
  }
  return said('none', null, 'No step open');
}

/**
 * The count over an account's products panel (BUGS.md D144): "2 open, 1 parked", where a parked deal was counted as open.
 * "one open" alone as before when nothing is parked.
 */
export function productsCountWords(rows: ReadonlyArray<{ lifecycle?: string }>): string {
  const parked = rows.filter((r) => r.lifecycle === 'paused').length;
  const open = rows.length - parked;
  const openWords = open === 1 ? 'one open' : `${open} open`;
  if (parked === 0) return openWords;
  return open === 0 ? `${parked} parked` : `${openWords}, ${parked} parked`;
}

/**
 * A parked deal's status, said before anything else on its line and its card: "Parked till 19 Oct: When he is back from
 * hols", the reason as the person who parked it wrote it. It stays on Deals, in Mine and in its product's ring (Mike, by
 * pop-up on 5 Oct 2026, "Everywhere, ring included"; BUGS.md D31, pipeline FT1). Null on an open deal.
 */
export function parkedWords(r: { lifecycle?: string; pausedUntil?: string | null; stateReason?: string | null }): StepWords | null {
  if (r.lifecycle !== 'paused') return null;
  const till = r.pausedUntil ? `Parked till ${dayMonth(new Date(r.pausedUntil))}` : 'Parked';
  const why = r.stateReason?.trim();
  return why ? said('parked', `${till}:`, why) : said('parked', null, till);
}

/**
 * BUGS.md X8, D9: a deal put aside to be sorted by hand, or archived (U222), in the company record's own words for them,
 * or null for a deal that is neither.
 */
export function putAsideWords(putAside: 'to_sort' | 'archived' | null | undefined): string | null {
  return putAside === 'to_sort' ? 'Put aside to be sorted by hand' : putAside === 'archived' ? 'Archived' : null;
}

/**
 * A status in its parts (P10, U306): its lead words ("Waiting on us:", "Your step:") and the rest, and whose move it is,
 * which the page colours by: what is ours to move (`us`, `mine`) leads in magenta; the client's, a colleague's step and
 * the show notes' do not. `words` is the whole, as it reads.
 */
export type StepKind = 'parked' | 'us' | 'client' | 'mine' | 'step' | 'notes' | 'none';
export interface StepWords { kind: StepKind; lead: string | null; rest: string; words: string; you: boolean; notes: boolean }
function said(kind: StepKind, lead: string | null, rest: string): StepWords {
  return { kind, lead, rest, words: lead ? `${lead} ${rest}` : rest, you: kind === 'mine', notes: kind === 'notes' };
}

/**
 * The line's owner column (U280): the "You" mark for the reader, else the owner's name, and the first name the phone's
 * second row says. "No owner" only where nobody holds it (U281 leaves that only where no source names anyone).
 */
export function ownerOf(r: HeatRow): { you: boolean; full: string; first: string } {
  if (r.holder?.mine) return { you: true, full: 'You', first: 'You' };
  const full = r.holder?.name?.trim();
  if (!full) return { you: false, full: r.holder ? 'A colleague' : 'No owner', first: r.holder ? 'A colleague' : 'No owner' };
  return { you: false, full, first: full.split(/\s+/)[0]! };
}

/** Who holds an opportunity, said as the account's owner is on a Deals line: "Yours", "Tom's". */
export function holderWords(r: HeatRow): string {
  if (r.holder?.mine) return 'Yours';
  const n = firstName(r.holder);
  return n ? possessive(n) : 'Nobody holds it yet';
}

/** The step box that leads an opened card (item 7): "Your step: <step>, by 9 Oct", or what holds it. */
export function stepBoxWords(r: HeatRow, now: string): StepWords & { mine: boolean; due: string | null; late: boolean } {
  const held = r.holds.filter((h) => h.blocks === 'next_step');
  const none = { mine: false, due: null, late: false };
  const parked = parkedWords(r);
  if (parked) return { ...parked, ...none };
  if (held.length > 0) return { ...said('us', 'Waiting on us:', held.map((h) => h.title).join('; ')), ...none };
  if (r.promises.length > 0) return { ...said('client', 'Waiting on them:', r.promises[0]!.statement), ...none };
  if (!r.step) {
    const noted = r.fromSheet?.nextStep?.trim();
    if (noted) {
      const who = r.fromSheet?.nextStepOwner?.trim();
      return { ...said('notes', null, `${who ? `${notesOwners(who)} step, from the show notes` : 'From the show notes'}: ${noted}`), ...none };
    }
    return { ...said('none', null, 'No step open'), ...none };
  }
  const due = r.step.dueAt ? dayMonth(new Date(r.step.dueAt)) : null;
  const mine = r.step.owner?.mine === true;
  return { ...said(mine ? 'mine' : 'step', `${stepOwner(r.step.owner)}:`, r.step.title), mine, due, late: r.step.dueAt !== null && r.step.dueAt < now };
}

/**
 * Where an opportunity's keenness came from, under the five buttons (item 7; states.html, "A keenness a person set"):
 * "Cobrain read 5 from Ana Ruiz's call, 29 Sep", "Tom set 4, 3 Oct. Cobrain read 5 and keeps Tom's.", "From the SBC
 * Summit notes". Until the brain lane's B5 reads keenness from mail, the sheet is said as the sheet (the brief, 6).
 */
export function keennessByWords(r: HeatRow, showName: string | null, dayStart: string | null): string {
  if (r.keenness === null) return 'No keenness yet: no client has said anything Cobrain could read.';
  const at = r.keennessAt ? dayWords(r.keennessAt, dayStart) : null;
  if (r.keennessSetBy === 'person') {
    const by = r.live?.keennessBy?.by ?? null;
    const who = by?.mine ? 'You' : firstName(by) ?? 'A colleague';
    const c = r.live?.keennessBy?.cobrain ?? null;
    const kept = c && c.keenness !== r.keenness ? ` Cobrain read ${c.keenness}${c.fromCall ? ' from the call' : ''} and keeps ${by?.mine ? 'yours' : possessive(who)}.` : '';
    return `${who} set ${r.keenness}${at ? `, ${at}` : ''}.${kept}`;
  }
  if (r.keennessSetBy === 'cobrain') {
    const m = r.live?.lastMove;
    if (m && m.field === 'keenness' && !m.undone && m.toCode === String(r.keenness)) {
      return `Cobrain read ${r.keenness}${m.from ? ` from ${messageWords(m.from)}` : ''}, ${dayWords(m.at, dayStart)}`;
    }
    return `Cobrain read ${r.keenness}${at ? `, ${at}` : ''}`;
  }
  if (r.keennessSetBy === 'sheet') return showName ? `From the ${showName} notes` : 'From the notes';
  return at ? `Set ${at}` : '';
}

/** Where a line sits on Deals (heat-shape.ts lineOrder): its place, the list's length, and how many share its keenness. */
export interface LineRank { pos: number; total: number; same: number }

/**
 * "Why is this here?" on the card (module 10 U263, Mike by pop-up on 4 Oct 2026: "Build it"), for the one ranked list
 * (U278): where the line sits and why, what breaks a tie among those at its keenness, where the keenness came from, and
 * the deal's place on the reader's list with Cobrain's reason. Every line is from the heat read the card already holds.
 */
export function whyHereWords(r: HeatRow, rank: LineRank, lead: Lead | null, showName: string | null, dayStart: string | null): { head: string; lines: string[] } {
  const head = r.keenness === null
    ? `Number ${rank.pos} of ${rank.total} on Deals: it has no keenness yet, so it comes after every opportunity with one.`
    : `Number ${rank.pos} of ${rank.total} on Deals, which runs by keenness: this one is at ${r.keenness}.`;
  const lines: string[] = [];
  if (rank.same > 1) {
    lines.push(`Among the ${rank.same} ${r.keenness === null ? 'with no keenness' : `at ${r.keenness}`}, the stage further along comes first, then the larger year's figure, then the latest the client said or did.`);
  }
  const by = keennessByWords(r, showName, dayStart);
  lines.push(r.keenness === null ? by : `Its keenness: ${by.replace(/\.$/, '')}.`);
  lines.push(lead ? `Number ${lead.rankNo} on your list today${lead.reason ? `: ${lead.reason.replace(/\.$/, '')}.` : '.'}` : 'It is not on your list today.');
  return { head, lines };
}

/** What the card says once a deal has been given on (U262, states.html's "Saved"): who holds it now. */
export function handedWords(to: { principalId: string; name: string }, me: string): string {
  if (to.principalId === me) return 'You hold it now.';
  return `${to.name.split(' ')[0] ?? to.name} holds it now.`;
}

/** Cobrain's change, said in parts so the card can set the value in bold (item 7; states.html). */
export interface MoveSentence { before: string; value: string; after: string }

export function moveSentence(m: CobrainMove, dayStart: string | null): MoveSentence {
  const what = m.field === 'stage' ? 'this' : 'the keenness';
  if (!m.undone) {
    return {
      before: `Cobrain moved ${what} to `, value: m.toName ?? '?',
      after: ` ${dayWords(m.at, dayStart)}${m.from ? `, from ${messageWords(m.from)}` : ''}.`,
    };
  }
  const who = m.undone.by?.mine ? 'You' : firstName(m.undone.by) ?? 'Someone';
  const back = m.fromName ?? (m.field === 'stage' ? 'where it was' : 'none');
  const source = m.from ? `that ${kindNoun(m.from.kind)}` : 'that message';
  return {
    before: `${who} put ${m.field === 'stage' ? 'it' : 'the keenness'} back to `, value: back,
    after: `, ${dayWords(m.undone.at, dayStart)}. Cobrain will not move it again from ${source}.`,
  };
}

/**
 * Whether the card still says what Cobrain changed: always while it stands; once put back, only while the field is still
 * what the Undo put back. A person who has moved it since has made the field theirs, and the history page has the story.
 */
export function moveStillSays(m: CobrainMove, now: { stageCode: string | null; keenness: number | null }): boolean {
  if (!m.undone) return true;
  return m.field === 'stage' ? m.fromCode === now.stageCode : m.fromCode === (now.keenness === null ? null : String(now.keenness));
}

/**
 * A decision of the team as the step box says it (B8, U328): "The team decided: <words>, by 9 Oct", and where it was
 * decided, "today, in the meeting “Core Commercial Team - Weekly 1”" (a meeting or call by its title, else the message
 * as the card names it).
 */
export function decisionWords(x: TeamDecision, dayStart: string | null): { lead: string; words: string; source: string } {
  const by = x.by ? `, by ${dayMonth(new Date(`${x.by}T12:00:00Z`))}` : '';
  const m = x.from;
  const where = !m ? null : (m.kind === 'meeting' || m.kind === 'call') && m.subject ? `the ${kindNoun(m.kind)} “${m.subject.trim()}”` : messageWords(m);
  return { lead: 'The team decided:', words: `${x.words.replace(/[.\s]+$/, '')}${by}.`, source: `${dayWords(x.at, dayStart)}${where ? `, in ${where}` : ''}` };
}

/** Whether a decision can still be taken back from the card: within D62's week. */
export function decisionUndoable(x: TeamDecision, now: string): boolean {
  return x.undoUntil > now;
}

/** "Lacerta Sports’ record", "Orbis Bet’s record": an account's name as an owner, its last s kept as it is said. */
export function accountPossessive(name: string | null): string {
  // BUGS.md D97: an account put away has no name to give; "Their".
  if (name === null || name.trim() === '') return 'Their';
  return /s$/i.test(name.trim()) ? `${name.trim()}’` : `${name.trim()}’s`;
}

/** D62's week: Undo is drawn while it lasts, and only on a move nobody has put back. */
export function undoable(m: CobrainMove, now: string): boolean {
  return m.undone === null && m.undoUntil !== null && m.undoUntil > now;
}

/** The page a message opens (search's rule, find/search.ts passagePath), or null: it is then listed with no press. */
export function opensPath(signalId: string, o: Opens | null, store: string): string | null {
  if (!o) return null;
  if (o.page === 'item') return `/items/${encodeURIComponent(o.id)}?store=${encodeURIComponent(store)}&signal=${encodeURIComponent(signalId)}#evidence`;
  return o.page === 'meeting' ? `/meetings/${encodeURIComponent(o.id)}` : `/chats/${encodeURIComponent(o.id)}`;
}

/** The stage meter's steps, by the store's own order and names (U153, U237): done, the one it is at, or ahead. */
export function meterSteps(stages: readonly StageStep[], code: string | null): Array<{ code: string; name: string; state: 'done' | 'at' | 'ahead' }> {
  const at = stages.findIndex((s) => s.code === code);
  return stages.map((s, i) => ({ code: s.code, name: s.name, state: at < 0 ? 'ahead' : i < at ? 'done' : i === at ? 'at' : 'ahead' }));
}

/** "2 still without one" under a line's figure, or nothing when every opportunity has one. */
export function withoutWords(m: HeatMoney): string | null {
  const n = m.of - m.withFigure;
  return n > 0 && m.withFigure > 0 ? `${n} still without one` : null;
}

/** "Latest" and its count of today's (item 7): "2 today, the newest first". */
export function latestHead(latest: readonly DealMessage[], todayCount: number): string {
  if (latest.length === 0) return '';
  return todayCount > 0 ? `${todayCount} today, the newest first` : 'the newest first';
}

// "Everything on this deal" (the history page, b-deal-page.html; the brief, section 5 item 9).

/** A message's own line in the history: who wrote it, and to whom when it was the reader. */
export function historySender(m: DealMessage): string {
  if (m.fromMe) return m.to ? `You, to ${m.to}` : 'You';
  return m.from ?? 'Someone';
}

/**
 * Who a change is said to be by: Cobrain, "You", a colleague by first name, or "The import". A change with source 'seed'
 * is any import (a seed loaded on 8 Sep, the show notes of 4 Oct, migration 176's stage remap), and the store keeps no
 * more than that, so the words say no more (live, 4 Oct: BetClic's InPlay Content, seeded on 8 Sep, read "The show notes").
 */
function sheetStageChange(c: HistoryChange): boolean {
  return c.source === 'seed' && c.field === 'stage_id' && c.from.code !== null && c.why?.startsWith("the team's account sheet,") === true;
}

function changer(c: HistoryChange): string {
  if (c.source === 'inferred') return 'Cobrain';
  if (c.by?.mine) return 'You';
  if (c.source === 'seed') return sheetStageChange(c) ? 'The sheet' : 'The import';
  return firstName(c.by) ?? 'Someone';
}

/** The deal's value as a change holds it, "9360.0 GBP", in the card's money: "£9,360". */
function dealValue(v: string | null): string | null {
  const m = v?.trim().match(/^(\d+(?:\.\d+)?)\s*([A-Z]{3})?$/);
  return m ? moneyFull(m[1]!, m[2] ?? null) : v;
}

/** A year's figure as a change holds it, or null when it was taken off. */
function yearFigure(state: Record<string, unknown> | null): string | null {
  const amount = state?.['revenue_year_amount'];
  const currency = state?.['revenue_year_currency'];
  if (amount === null || amount === undefined) return null;
  return `${moneyFull(String(amount), typeof currency === 'string' && currency ? currency : null)} a year`;
}

/**
 * One change in the history, as a head and the line under it: what Cobrain moved or read and from which message, what a
 * person set and their one line why, and what an undo put back.
 */
export function changeWords(c: HistoryChange): { head: string; body: string | null } {
  const who = changer(c);
  const back = c.undoes !== null;
  const to = c.to.name ?? c.to.code;
  const source = c.message ? `From ${messageWords(c.message)}.` : null;
  const why = c.why ?? (typeof c.newState?.['keenness_why'] === 'string' ? c.newState['keenness_why'] as string : null);
  switch (c.field) {
    case 'stage_id':
      // A first stage, an import's, was set rather than moved.
      return { head: `${who} ${back ? 'put the stage back to' : c.from.code === null ? 'set the stage to' : 'moved the stage to'} ${to ?? 'no stage'}`, body: back ? null : source ?? (sheetStageChange(c) ? why : null) };
    case 'keenness': {
      const raw = c.newState && 'keenness' in c.newState ? c.newState['keenness'] : to;
      const k = raw === null || raw === undefined ? 'none' : String(raw);
      if (back) return { head: `${who} put the keenness back to ${k}`, body: null };
      if (c.source === 'inferred') return { head: `Cobrain read the keenness as ${k}`, body: source };
      return { head: `${who} set the keenness to ${k}`, body: why ? `“${why}”` : null };
    }
    case 'revenue_year': {
      const f = yearFigure(c.newState);
      if (back) return { head: f ? `${who} put the revenue potential back to ${f}` : `${who} took the revenue potential off again`, body: null };
      return { head: f ? `${who} set the revenue potential to ${f}` : `${who} took the revenue potential off`, body: source };
    }
    case 'product':
      return { head: `${who} ${back ? 'put the product back to' : 'made the product'} ${to ?? 'none'}`, body: null };
    case 'paused_until': {
      // BUGS.md D145: the park says why, from the reason written with it.
      const reason = why ?? (c.to.code && typeof c.newState?.['state_reason'] === 'string' ? c.newState['state_reason'] as string : null);
      return { head: c.to.code ? `${who} parked it until ${dayMonth(new Date(c.to.code))}` : `${who} took it out of parked`, body: reason ? `“${reason}”` : null };
    }
    case 'value_amount': {
      const v = dealValue(c.to.code);
      return { head: v ? `${who} ${back ? 'put the deal\'s value back to' : 'set the deal\'s value to'} ${v}` : `${who} took the deal's value off`, body: null };
    }
    // BUGS.md D145: these read "changed the state reason", "changed the pay", "changed the base" with nothing after.
    case 'state_reason':
      return c.to.code ? { head: `${who} gave the reason`, body: `“${c.to.code}”` } : { head: `${who} took the reason off`, body: null };
    case 'pay': {
      const p = payChangeWords(c.newState);
      return { head: p ? `${who} ${back ? 'put how it pays back to' : 'set how it pays:'} ${p}` : `${who} took how it pays off`, body: back ? null : source };
    }
    case 'base': {
      // The new value is "<amount> <period>" ("120000.00 month"); the state, where written, says the same.
      const [codeAmount, codePeriod] = (c.to.code ?? '').trim().split(/\s+/);
      const amount = c.newState && 'base_amount' in c.newState ? c.newState['base_amount'] : codeAmount || null;
      const period = (c.newState?.['base_period'] as string | undefined) ?? codePeriod;
      const b = amount === null || amount === undefined ? null : `${moneyFull(String(amount), null)} ${period === 'month' ? 'a month' : 'a year'}`;
      return { head: b ? `${who} ${back ? 'put their base back to' : 'set their base to'} ${b}` : `${who} took their base off`, body: back ? null : source };
    }
    default:
      return { head: `${who} changed the ${c.field.replace(/_/g, ' ')}${c.to.code ? ` to ${c.to.name ?? c.to.code}` : ''}`, body: null };
  }
}

/** How a deal pays, from a pay change's new state (BUGS.md D145), in the card's words: "fixed fee", "revenue share", "per match". */
function payChangeWords(st: Record<string, unknown> | null): string | null {
  const basis = typeof st?.['pay_basis'] === 'string' ? st['pay_basis'] as string : null;
  if (basis === null) return null;
  const cur = typeof st?.['pay_currency'] === 'string' ? st['pay_currency'] as string : null;
  const per = (p: unknown) => (p === 'month' ? 'a month' : 'a year');
  if (basis === 'fixed') return st?.['fee_amount'] ? `fixed fee, ${moneyFull(String(st['fee_amount']), cur)} ${per(st['fee_period'])}` : 'fixed fee';
  if (basis === 'share') return st?.['share_pct'] ? `revenue share, ${Number(st['share_pct']).toLocaleString('en-GB', { maximumFractionDigits: 3 })}%` : 'revenue share';
  if (basis === 'per_match') return st?.['match_rate'] ? `per match, ${moneyFull(String(st['match_rate']), cur)} a match` : 'per match';
  return 'not known yet';
}

/** Cobrain's change may be undone from the history while its week lasts and nobody has put it back. */
export function historyUndoable(c: HistoryChange, now: string): boolean {
  return (c.source === 'inferred' || sheetStageChange(c)) && c.undoes === null && c.undoneBy === null && c.undoUntil !== null && c.undoUntil > now;
}

/**
 * One act on the deal itself in the history (BUGS.md D53, D142, D146): who added, handed, gave, took, removed it or brought
 * a history in, and to whom.
 */
export function actWords(a: DealAct): string {
  const who = a.by?.mine ? 'You' : firstName(a.by) ?? 'Someone';
  const to = a.to?.mine ? 'you' : firstName(a.to) ?? 'a colleague';
  switch (a.kind) {
    case 'add': return `${who} added the deal`;
    case 'hand': return `${who} handed it to ${to}`;
    case 'give': return `${who} gave it to ${to}`;
    case 'take': return `${who} took it`;
    case 'remove': return `${who} removed it`;
    case 'join': return a.title ? `${who} brought in the history of ${a.title}` : `${who} brought in a history from another record`;
    // BUGS.md D54: sorted by hand from the deals put aside.
    case 'keep': return `${who} kept it from the deals put aside`;
    case 'archive': return `${who} archived it from the deals put aside`;
    case 'won': return `${who} marked it won`;
    case 'lost': return `${who} marked it lost`;
  }
}

/** An act may be undone from the history by the person who did it, while its week lasts and it has not been undone. */
export function actUndoable(a: DealAct, now: string): boolean {
  return a.by?.mine === true && a.undoneAt === null && a.undoUntil !== null && a.undoUntil > now;
}

/** The act's Undo, as Mike chose it (FT2, 6 Oct): "Undo until 14 Oct". */
export function actUndoWords(a: DealAct): string {
  return a.undoUntil ? `Undo until ${dayMonth(new Date(a.undoUntil))}` : 'Undo';
}
